import React, { useEffect, useMemo, useState } from "react";
import { Layout } from "./../../../components/Layout/Layout";
import { useParams } from "react-router-dom";
import useFetchData from "../../../hooks/FetchData";
import moment from "moment";
import ProfileImg from "../../../assets/profile.png";
import StopImg from "../../../assets/icons/stop.svg";
import { http } from "../../../utils/httpCommon";
import refershIcon from "../../../assets/icons/refresh-cw.svg";
import { useAuthUser } from "react-auth-kit";
import { prettyNumber } from "../../../lib/prettyNumber";
import ModalComponent from "../../../components/Modal/Modal";

/** Format booking day line for display (handles unix-style start/end times). */
function formatBookingDayLine(day) {
  if (!day) return "—";
  const datePart = day.date ? moment(day.date).format("LL") : "—";
  if (day.startTime == null || day.endTime == null) return datePart;
  const fmt = (v) => {
    if (typeof v !== "number") return String(v);
    if (v > 1e11) return moment(v).format("HH:mm");
    if (v > 1e9) return moment.unix(v).format("HH:mm");
    const h = Math.floor(v / 3600);
    const m = Math.floor((v % 3600) / 60);
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  };
  return `${datePart} · ${fmt(day.startTime)} – ${fmt(day.endTime)}`;
}

function PromoBookingDetailModal({ booking, opened, setOpened }) {
  const user = booking?.user;
  const clientName = typeof user === "object" && user?.name != null ? user.name : "—";
  const contact =
    typeof user === "object" ? user?.email || user?.phone || "—" : "—";
  const locName =
    typeof booking?.location === "object" && booking?.location?.name != null
      ? booking.location.name
      : "—";
  const facName =
    typeof booking?.facility === "object" && booking?.facility?.name != null
      ? booking.facility.name
      : "—";

  return (
    <ModalComponent opened={opened} setOpened={setOpened} size="lg">
      <div className="text-[20px] mb-4" style={{ fontFamily: "gilroy-bold" }}>
        Booking details
      </div>
      <div className="flex flex-col gap-3 text-[16px]">
        <div className="flex flex-col">
          <span className="text-[#797979]">Booking ID</span>
          <span style={{ fontFamily: "gilroy-bold" }}>
            {booking?._id != null ? String(booking._id) : "—"}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-[#797979]">Client</span>
          <span style={{ fontFamily: "gilroy-bold" }}>{clientName}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[#797979]">Contact</span>
          <span>{contact}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[#797979]">Location</span>
          <span style={{ fontFamily: "gilroy-bold" }}>{locName}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[#797979]">Facility</span>
          <span style={{ fontFamily: "gilroy-bold" }}>{facName}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[#797979]">Redemption date</span>
          <span style={{ fontFamily: "gilroy-bold" }}>
            {booking?.createdAt ? moment(booking.createdAt).format("LLL") : "—"}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-[#797979]">Status</span>
          <span style={{ fontFamily: "gilroy-bold" }}>
            {booking?.status ?? "—"}
            {booking?.paymentStatus != null ? ` · ${booking.paymentStatus}` : ""}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-[#797979]">Promo amount</span>
          <span style={{ fontFamily: "gilroy-bold" }}>
            {booking?.promoCodeAmount != null ? prettyNumber(booking.promoCodeAmount) : "—"}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-[#797979]">Sessions / days</span>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            {(booking?.days || []).length === 0 ? (
              <li className="text-[#797979]">—</li>
            ) : (
              booking.days.map((d, i) => (
                <li key={d?._id ?? i}>{formatBookingDayLine(d)}</li>
              ))
            )}
          </ul>
        </div>
      </div>
    </ModalComponent>
  );
}

function PromoCheckInDetailModal({ checkIn, opened, setOpened }) {
  const user = checkIn?.user;
  const clientName = typeof user === "object" && user?.name != null ? user.name : "—";
  const contact =
    typeof user === "object" ? user?.email || user?.phone || "—" : "—";
  const locName =
    typeof checkIn?.location === "object" && checkIn?.location?.name != null
      ? checkIn.location.name
      : "—";
  const facName =
    typeof checkIn?.facility === "object" && checkIn?.facility?.name != null
      ? checkIn.facility.name
      : "—";
  const redemptionDate = checkIn?.checkInTime || checkIn?.createdAt;

  return (
    <ModalComponent opened={opened} setOpened={setOpened} size="lg">
      <div className="text-[20px] mb-4" style={{ fontFamily: "gilroy-bold" }}>
        Check-in details
      </div>
      <div className="flex flex-col gap-3 text-[16px]">
        <div className="flex flex-col">
          <span className="text-[#797979]">Check-in ID</span>
          <span style={{ fontFamily: "gilroy-bold" }}>
            {checkIn?._id != null ? String(checkIn._id) : "—"}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-[#797979]">Client</span>
          <span style={{ fontFamily: "gilroy-bold" }}>{clientName}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[#797979]">Contact</span>
          <span>{contact}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[#797979]">Location</span>
          <span style={{ fontFamily: "gilroy-bold" }}>{locName}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[#797979]">Facility</span>
          <span style={{ fontFamily: "gilroy-bold" }}>{facName}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[#797979]">Redemption date</span>
          <span style={{ fontFamily: "gilroy-bold" }}>
            {redemptionDate ? moment(redemptionDate).format("LLL") : "—"}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-[#797979]">Status</span>
          <span style={{ fontFamily: "gilroy-bold" }}>
            {checkIn?.paymentStatus ?? "—"}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-[#797979]">Promo amount</span>
          <span style={{ fontFamily: "gilroy-bold" }}>
            {checkIn?.promoCodeAmount != null ? prettyNumber(checkIn.promoCodeAmount) : "—"}
          </span>
        </div>
      </div>
    </ModalComponent>
  );
}

const USAGE_TABS = {
  MEETING_ROOMS: "meeting_rooms",
  SHARED_SPACES: "shared_spaces",
};

const PromoCode = () => {
  const params = useParams();
  const auth = useAuthUser();
  const { response, loading, fetchData } = useFetchData(`/promo-codes/${params.id}`);
  const {
    response: bookingsRaw,
    fetchData: fetchBookingsData,
  } = useFetchData(params.id ? `/promo-codes/${params.id}/bookings` : null);
  const {
    response: checkInsRaw,
    fetchData: fetchCheckInsData,
  } = useFetchData(params.id ? `/promo-codes/${params.id}/check-ins` : null);

  const bookingsList = Array.isArray(bookingsRaw) ? bookingsRaw : [];
  const checkInsList = Array.isArray(checkInsRaw) ? checkInsRaw : [];

  const bookingsByClientId = useMemo(() => {
    const map = new Map();
    for (const b of bookingsList) {
      const uid = b?.user?._id ?? b?.user;
      if (uid == null) continue;
      const key = String(uid);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(b);
    }
    return map;
  }, [bookingsList]);

  const checkInsByClientId = useMemo(() => {
    const map = new Map();
    for (const c of checkInsList) {
      const uid = c?.user?._id ?? c?.user;
      if (uid == null) continue;
      const key = String(uid);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    }
    return map;
  }, [checkInsList]);

  const sharedSpaceUtilization = useMemo(() => {
    const rows = [];
    for (const [clientId, checkIns] of checkInsByClientId.entries()) {
      rows.push({
        client: checkIns[0]?.user,
        count: checkIns.length,
        clientId,
      });
    }
    return rows.sort((a, b) => b.count - a.count);
  }, [checkInsByClientId]);

  const sharedSpaceTotalUses = checkInsList.length;

  const [usageTab, setUsageTab] = useState(USAGE_TABS.MEETING_ROOMS);
  const [expandedClients, setExpandedClients] = useState(() => new Set());
  const [modalBooking, setModalBooking] = useState(null);
  const [modalCheckIn, setModalCheckIn] = useState(null);

  const toggleExpand = (clientKey) => {
    setExpandedClients((prev) => {
      const next = new Set(prev);
      if (next.has(clientKey)) next.delete(clientKey);
      else next.add(clientKey);
      return next;
    });
  };

  const TogglePromoCode = async () => {
    let data;
    response?.status === "inactive"
      ? (data = JSON.stringify({
          status: "active",
        }))
      : (data = JSON.stringify({
          status: "inactive",
        }));

    try {
      await http.patch(`/promo-codes/${params.id}`, data);
      fetchData();
      fetchBookingsData();
      fetchCheckInsData();
    } catch (error) {
      console.log("error", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [loading]);

  return (
    <Layout>
      <div className="w-[60%] m-4 flex flex-col gap-3">
        <div
          className=" text-[26px] flex  justify-between gap-4  my-2"
          style={{ fontFamily: "gilroy-bold" }}
        >
          <div className="flex items-center gap-2" style={{ fontFamily: "gilroy-bold" }}>
            Promo Code
            <div className="flex gap-[10px] items-center ">
              <div
                className={` ${
                  response?.status === "active"
                    ? "text-[#00AD3B]  bg-[#00ad3b1a] "
                    : "text-[#797979]  bg-[#E4E4E4]"
                } text-[16px]  font-semibold py-1 px-2 flex  justify-center items-center rounded-[25px]`}
              >
                {response?.status}
              </div>
            </div>
          </div>
          {auth().role !== "Land Lord" && (
            <>
              {response?.status === "active" ? (
                <button
                  onClick={TogglePromoCode}
                  className="flex gap-3 text-[20px] items-center text-[#011516]"
                >
                  <img src={StopImg} className="w-[30px] h-[30px]" alt="stop" />
                  Stop promo Code
                </button>
              ) : (
                <button
                  onClick={TogglePromoCode}
                  className="flex gap-3 text-[20px]  items-center text-[#00AD3B]"
                >
                  <img src={refershIcon} className="w-[30px] h-[30px]" alt="resume" />
                  Resume code
                </button>
              )}
            </>
          )}
        </div>
        <div className="bg-white w-fit min-w-[60%]  px-6 py-4  ">
          <div className="flex gap-6">
            <div className="text-[18px]" style={{ fontFamily: "gilroy-bold" }}>
              Basic info{" "}
            </div>
            {/* <button
            onClick={() => {
              setStep(0);
            }}
            className="group text-[16px] text-[#686868] flex items-center hover:text-[#32B3BE] transition-all "
          >
            <div className="flex justify-center items-center bg-[#686868] w-[30px] h-[30px] transition-all rounded-[10px] mr-2 group-hover:bg-[#32B3BE]">
              <img className=" w-[18px] " src={whiteEdit} alt="edit" />
            </div>
            Edit
          </button> */}
          </div>
          <div className="flex flex-col gap-1 text-[18px] ">
            <div className="flex flex-col mt-2 ">
              <span className="text-[#797979]">Name</span>
              <span style={{ fontFamily: "gilroy-bold" }}>{response?.name}</span>
            </div>
            <div className="flex flex-col mt-2 ">
              <span className="text-[#797979]">Code</span>
              <span style={{ fontFamily: "gilroy-bold" }}>{response?.code}</span>
            </div>
            <div className="flex flex-col mt-2 ">
              <span className="text-[#797979]">Code Type</span>
              <span style={{ fontFamily: "gilroy-bold" }}>{response?.type}</span>
            </div>
            <div className="flex flex-col mt-2 ">
              <span className="text-[#797979] font-extrabold">Value</span>
              <span style={{ fontFamily: "gilroy-bold" }}>{prettyNumber(response?.value)}</span>
            </div>
            <div className="flex flex-col mt-2 ">
              <span className="text-[#797979]">Corporate promo code</span>
              <span style={{ fontFamily: "gilroy-bold" }}>
                {response?.isCorporate ? "Yes" : "No"}
              </span>
            </div>
            <div className=" mt-2 flex gap-16 ">
              <div className="flex flex-col">
                <span className="text-[#797979]">Starting Date</span>
                <span style={{ fontFamily: "gilroy-bold" }}>
                  {moment(response?.startDate).format("LL")}
                  {/* 4th, Sept 2021 */}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-[#797979]">Ending Date</span>
                <span style={{ fontFamily: "gilroy-bold" }}>
                  {moment(response?.endDate).format("LL")}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex w-full max-w-4xl min-w-[60%] text-[18px] font-semibold">
          <button
            type="button"
            onClick={() => setUsageTab(USAGE_TABS.MEETING_ROOMS)}
            className={`${
              usageTab === USAGE_TABS.MEETING_ROOMS
                ? "text-black border-b-[#32B3BE] border-b-[6px]"
                : "text-[#8E8E8E] border-b-[#EFEFEF] border-b-[6px]"
            } flex font-semibold w-fit min-w-fit transition-all duration-150 underline-offset-8`}
          >
            <div className="px-2 min-w-[135px] w-fit">Meeting rooms</div>
          </button>
          <button
            type="button"
            onClick={() => setUsageTab(USAGE_TABS.SHARED_SPACES)}
            className={`${
              usageTab === USAGE_TABS.SHARED_SPACES
                ? "text-black border-b-[#32B3BE] border-b-[6px]"
                : "text-[#8E8E8E] border-b-[#EFEFEF] border-b-[6px]"
            } flex font-semibold w-fit min-w-fit transition-all duration-150 underline-offset-8`}
          >
            <div className="px-2 min-w-[135px] w-fit">Shared spaces</div>
          </button>
        </div>

        <div className="bg-white w-full max-w-4xl min-w-[60%] px-6 py-4">
          <div className="text-[18px] mb-4" style={{ fontFamily: "gilroy-bold" }}>
            Usage summary
          </div>
          <div className="flex flex-col gap-3 text-[18px]">
            <div className="flex flex-col">
              <span className="text-[#797979]">Usage type</span>
              <span style={{ fontFamily: "gilroy-bold" }}>
                {response?.usage?.type === "per_user"
                  ? "per user"
                  : response?.usage?.type ?? "—"}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-[#797979]">Total uses</span>
              <span style={{ fontFamily: "gilroy-bold" }}>
                {response?.usage?.type === "per_user"
                  ? prettyNumber(
                      usageTab === USAGE_TABS.SHARED_SPACES
                        ? sharedSpaceTotalUses
                        : (response?.usage?.used ?? 0)
                    )
                  : `${prettyNumber(
                      usageTab === USAGE_TABS.SHARED_SPACES
                        ? sharedSpaceTotalUses
                        : (response?.usage?.used ?? 0)
                    )} / ${prettyNumber(response?.usage?.count ?? 0)}`}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-[#797979]">Per-user limit</span>
              <span style={{ fontFamily: "gilroy-bold" }}>
                {response?.usage?.type === "per_user"
                  ? prettyNumber(response?.usage?.perUser ?? 0)
                  : response?.usage?.perUser != null
                    ? prettyNumber(response.usage.perUser)
                    : "—"}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white w-full max-w-4xl min-w-[60%] px-6 py-4 overflow-x-auto">
          <div className="text-[18px] mb-4" style={{ fontFamily: "gilroy-bold" }}>
            Utilization by client
          </div>
          {usageTab === USAGE_TABS.MEETING_ROOMS ? (
            !response?.utilization?.length ? (
              <p className="text-[#797979] text-[16px]">No usage yet.</p>
            ) : (
              <table className="w-full text-left text-[16px] border-collapse">
                <thead>
                  <tr className="border-b border-[#E4E4E4]">
                    <th className="py-2 pr-2 w-10 text-[#797979] font-normal" aria-label="Expand" />
                    <th className="py-2 pr-4 text-[#797979] font-normal">Client</th>
                    <th className="py-2 pr-4 text-[#797979] font-normal">Contact</th>
                    <th className="py-2 text-[#797979] font-normal text-right">Times used</th>
                  </tr>
                </thead>
                <tbody>
                  {(response?.utilization ?? []).map((row, index) => {
                    const client = row?.client;
                    const clientId =
                      typeof client === "object" && client?._id != null
                        ? String(client._id)
                        : client != null
                          ? String(client)
                          : `row-${index}`;
                    const name =
                      typeof client === "object" && client?.name != null
                        ? client.name
                        : "Unknown client";
                    const contact =
                      typeof client === "object"
                        ? client?.email || client?.phone || "—"
                        : "—";
                    const avatar =
                      typeof client === "object" && client?.profilePicture
                        ? client.profilePicture
                        : ProfileImg;
                    const expanded = expandedClients.has(clientId);
                    const clientBookings = bookingsByClientId.get(clientId) || [];

                    return (
                      <React.Fragment key={clientId}>
                        <tr className="border-b border-[#F0F0F0]">
                          <td className="py-3 pr-2 align-middle">
                            <button
                              type="button"
                              onClick={() => toggleExpand(clientId)}
                              className="w-8 h-8 flex items-center justify-center rounded hover:bg-[#F5F5F5] text-[#797979]"
                              aria-expanded={expanded}
                              aria-label={expanded ? "Collapse details" : "Expand details"}
                            >
                              <span
                                className="inline-block transition-transform duration-200"
                                style={{ transform: expanded ? "rotate(90deg)" : "rotate(0deg)" }}
                              >
                                ▸
                              </span>
                            </button>
                          </td>
                          <td className="py-3 pr-4">
                            <div className="flex items-center gap-3">
                              <img
                                src={avatar}
                                alt=""
                                className="w-10 h-10 object-cover rounded-full shrink-0"
                              />
                              <span style={{ fontFamily: "gilroy-bold" }}>{name}</span>
                            </div>
                          </td>
                          <td className="py-3 pr-4 text-[#797979]">{contact}</td>
                          <td className="py-3 text-right" style={{ fontFamily: "gilroy-bold" }}>
                            {prettyNumber(row?.count ?? 0)}
                          </td>
                        </tr>
                        {expanded && (
                          <tr className="border-b border-[#F0F0F0] bg-[#FAFAFA]">
                            <td colSpan={4} className="py-3 px-2 pl-12">
                              {clientBookings.length === 0 ? (
                                <p className="text-[#797979] text-[14px] py-2">
                                  No booking records found for this client (details may be
                                  unavailable if bookings were removed).
                                </p>
                              ) : (
                                <table className="w-full text-left text-[14px] border-collapse">
                                  <thead>
                                    <tr className="border-b border-[#E4E4E4]">
                                      <th className="py-2 pr-4 text-[#797979] font-normal">
                                        Location
                                      </th>
                                      <th className="py-2 pr-4 text-[#797979] font-normal">
                                        Facility
                                      </th>
                                      <th className="py-2 pr-4 text-[#797979] font-normal">
                                        Date of redemption
                                      </th>
                                      <th className="py-2 text-[#797979] font-normal text-right">
                                        Booking
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {clientBookings.map((b, bookingIdx) => {
                                      const bid =
                                        b?._id != null ? String(b._id) : `idx-${bookingIdx}`;
                                      const loc =
                                        typeof b?.location === "object" &&
                                        b?.location?.name != null
                                          ? b.location.name
                                          : "—";
                                      const fac =
                                        typeof b?.facility === "object" &&
                                        b?.facility?.name != null
                                          ? b.facility.name
                                          : "—";
                                      const redeemed = b?.createdAt
                                        ? moment(b.createdAt).format("LLL")
                                        : "—";
                                      return (
                                        <tr key={bid} className="border-b border-[#EEEEEE]">
                                          <td
                                            className="py-2 pr-4"
                                            style={{ fontFamily: "gilroy-bold" }}
                                          >
                                            {loc}
                                          </td>
                                          <td
                                            className="py-2 pr-4"
                                            style={{ fontFamily: "gilroy-bold" }}
                                          >
                                            {fac}
                                          </td>
                                          <td className="py-2 pr-4 text-[#797979]">{redeemed}</td>
                                          <td className="py-2 text-right">
                                            <button
                                              type="button"
                                              className="text-[#32B3BE] hover:underline font-semibold"
                                              onClick={() => setModalBooking(b)}
                                            >
                                              View booking
                                            </button>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )
          ) : !sharedSpaceUtilization.length ? (
            <p className="text-[#797979] text-[16px]">No usage yet.</p>
          ) : (
            <table className="w-full text-left text-[16px] border-collapse">
              <thead>
                <tr className="border-b border-[#E4E4E4]">
                  <th className="py-2 pr-2 w-10 text-[#797979] font-normal" aria-label="Expand" />
                  <th className="py-2 pr-4 text-[#797979] font-normal">Client</th>
                  <th className="py-2 pr-4 text-[#797979] font-normal">Contact</th>
                  <th className="py-2 text-[#797979] font-normal text-right">Times used</th>
                </tr>
              </thead>
              <tbody>
                {sharedSpaceUtilization.map((row, index) => {
                  const client = row?.client;
                  const clientId =
                    row?.clientId ??
                    (typeof client === "object" && client?._id != null
                      ? String(client._id)
                      : client != null
                        ? String(client)
                        : `row-${index}`);
                  const name =
                    typeof client === "object" && client?.name != null
                      ? client.name
                      : "Unknown client";
                  const contact =
                    typeof client === "object"
                      ? client?.email || client?.phone || "—"
                      : "—";
                  const avatar =
                    typeof client === "object" && client?.profilePicture
                      ? client.profilePicture
                      : ProfileImg;
                  const expanded = expandedClients.has(clientId);
                  const clientCheckIns = checkInsByClientId.get(clientId) || [];

                  return (
                    <React.Fragment key={clientId}>
                      <tr className="border-b border-[#F0F0F0]">
                        <td className="py-3 pr-2 align-middle">
                          <button
                            type="button"
                            onClick={() => toggleExpand(clientId)}
                            className="w-8 h-8 flex items-center justify-center rounded hover:bg-[#F5F5F5] text-[#797979]"
                            aria-expanded={expanded}
                            aria-label={expanded ? "Collapse details" : "Expand details"}
                          >
                            <span
                              className="inline-block transition-transform duration-200"
                              style={{ transform: expanded ? "rotate(90deg)" : "rotate(0deg)" }}
                            >
                              ▸
                            </span>
                          </button>
                        </td>
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-3">
                            <img
                              src={avatar}
                              alt=""
                              className="w-10 h-10 object-cover rounded-full shrink-0"
                            />
                            <span style={{ fontFamily: "gilroy-bold" }}>{name}</span>
                          </div>
                        </td>
                        <td className="py-3 pr-4 text-[#797979]">{contact}</td>
                        <td className="py-3 text-right" style={{ fontFamily: "gilroy-bold" }}>
                          {prettyNumber(row?.count ?? 0)}
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="border-b border-[#F0F0F0] bg-[#FAFAFA]">
                          <td colSpan={4} className="py-3 px-2 pl-12">
                            {clientCheckIns.length === 0 ? (
                              <p className="text-[#797979] text-[14px] py-2">
                                No check-in records found for this client (details may be
                                unavailable if check-ins were removed).
                              </p>
                            ) : (
                              <table className="w-full text-left text-[14px] border-collapse">
                                <thead>
                                  <tr className="border-b border-[#E4E4E4]">
                                    <th className="py-2 pr-4 text-[#797979] font-normal">
                                      Location
                                    </th>
                                    <th className="py-2 pr-4 text-[#797979] font-normal">
                                      Facility
                                    </th>
                                    <th className="py-2 pr-4 text-[#797979] font-normal">
                                      Date of redemption
                                    </th>
                                    <th className="py-2 text-[#797979] font-normal text-right">
                                      Booking
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {clientCheckIns.map((c, checkInIdx) => {
                                    const cid =
                                      c?._id != null ? String(c._id) : `idx-${checkInIdx}`;
                                    const loc =
                                      typeof c?.location === "object" &&
                                      c?.location?.name != null
                                        ? c.location.name
                                        : "—";
                                    const fac =
                                      typeof c?.facility === "object" &&
                                      c?.facility?.name != null
                                        ? c.facility.name
                                        : "—";
                                    const redeemed =
                                      c?.checkInTime || c?.createdAt
                                        ? moment(c.checkInTime || c.createdAt).format("LLL")
                                        : "—";
                                    return (
                                      <tr key={cid} className="border-b border-[#EEEEEE]">
                                        <td
                                          className="py-2 pr-4"
                                          style={{ fontFamily: "gilroy-bold" }}
                                        >
                                          {loc}
                                        </td>
                                        <td
                                          className="py-2 pr-4"
                                          style={{ fontFamily: "gilroy-bold" }}
                                        >
                                          {fac}
                                        </td>
                                        <td className="py-2 pr-4 text-[#797979]">{redeemed}</td>
                                        <td className="py-2 text-right">
                                          <button
                                            type="button"
                                            className="text-[#32B3BE] hover:underline font-semibold"
                                            onClick={() => setModalCheckIn(c)}
                                          >
                                            View booking
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <PromoBookingDetailModal
        booking={modalBooking}
        opened={modalBooking != null}
        setOpened={(open) => {
          if (!open) setModalBooking(null);
        }}
      />
      <PromoCheckInDetailModal
        checkIn={modalCheckIn}
        opened={modalCheckIn != null}
        setOpened={(open) => {
          if (!open) setModalCheckIn(null);
        }}
      />
    </Layout>
  );
};

export default PromoCode;
